import React, { useEffect, useState } from 'react';
import { Check, TrendingUp, Crown, Eye, Tag } from 'lucide-react';
import { fetchPublicBilling } from '../services/apiClient';

// Presentation-layer pricing. Drop L (Stripe billing) will wire these to the
// admin-editable prices; for now they reflect the recommended launch structure.
const TIERS = [
  {
    id: 'observer', name: 'Observer', icon: Eye, accent: '#64748b',
    monthly: 0, annual: 0, tagline: 'Start free',
    features: ['Master signal light + plain-English read', 'Learn center basics', 'Demo / scenario mode'],
    cta: 'Get Started',
  },
  {
    id: 'pro', name: 'Pro', icon: TrendingUp, accent: '#2563eb', featured: true,
    monthly: 15, annual: 129, tagline: 'Market Awareness',
    features: ['Full live signal + honesty badges', 'All detail tabs (Yield Curve, Credit, Flows, Strategist)', 'Regime Alerts + full Learn + Simulator'],
    cta: 'Choose Pro',
  },
  {
    id: 'premium', name: 'Strategist', icon: Crown, accent: '#a855f7',
    monthly: 39, annual: 390, tagline: 'For power users',
    features: ['Everything in Pro', 'Historical Replay, Backtesting, Probability', 'Risk Profiles + priority support + early access'],
    cta: 'Choose Strategist',
  },
];

export default function SalesPage({ siteMode, onRequestAccess, onSignIn }) {
  const sales = siteMode?.sales || {};
  // Show monthly first — the lower number is less of a sticker-shock anchor, and
  // visitors can switch to Annual to see the savings.
  const [annual, setAnnual] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    let active = true;
    fetchPublicBilling().then((result) => { if (active && result?.ok) setBilling(result); });
    return () => { active = false; };
  }, []);

  // Overlay any admin-edited prices onto the display tiers.
  const livePrice = (id, field) => {
    const map = { observer: 'observer', pro: 'pro', premium: 'premium' };
    const t = billing?.tiers?.[map[id]];
    return t ? t[field] : null;
  };
  const offers = billing?.offers || [];

  return (
    <div className="min-h-screen bg-mentor-bg text-mentor-text">
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h1 className="font-display font-black text-4xl sm:text-5xl mb-4">{sales.headline || 'Bond Mentor is live.'}</h1>
          <p className="max-w-2xl mx-auto text-mentor-muted text-lg leading-relaxed">
            {sales.subMessage || 'Read the bond market in plain English. Choose the plan that fits how you invest.'}
          </p>

          <div className="inline-flex items-center gap-1 mt-7 rounded-full border border-mentor-border/60 bg-mentor-bg/40 p-1">
            <button type="button" onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-sm font-display font-bold transition-colors ${!annual ? 'bg-mentor-accent/20 text-mentor-accent' : 'text-mentor-muted'}`}>Monthly</button>
            <button type="button" onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-sm font-display font-bold transition-colors ${annual ? 'bg-mentor-accent/20 text-mentor-accent' : 'text-mentor-muted'}`}>Annual <span className="text-signal-green">· save</span></button>
          </div>
        </div>

        {/* Live limited-seat offers */}
        {offers.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {offers.map((o) => (
              <button key={o.id} type="button" onClick={onRequestAccess} className="inline-flex items-center gap-2 rounded-full border border-mentor-accent/50 bg-mentor-accent/10 px-4 py-2 text-sm hover:bg-mentor-accent/20 transition-colors">
                <Tag size={14} className="text-mentor-accent" />
                <span className="font-display font-bold text-mentor-text">{o.name}</span>
                <span className="text-mentor-muted">${o.price}{o.termMonths === 12 ? '/yr' : `/${o.termMonths}mo`}</span>
                {o.seatCap > 0 && <span className="text-xs text-mentor-accent">· {o.seatsLeft} of {o.seatCap} left</span>}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const liveMonthly = livePrice(tier.id, 'monthly');
            const liveAnnual = livePrice(tier.id, 'annual');
            const monthlyVal = liveMonthly ?? tier.monthly;
            const annualVal = liveAnnual ?? tier.annual;
            const price = annual ? annualVal : monthlyVal;
            const unit = monthlyVal === 0 ? '' : annual ? '/year' : '/month';
            return (
              <div key={tier.id} className={`glass-card p-6 border ${tier.featured ? 'border-mentor-accent/60 ring-1 ring-mentor-accent/30' : 'border-mentor-border/55'} flex flex-col relative`}>
                {tier.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-mentor-accent text-white text-[10px] font-display font-bold uppercase tracking-wider">Most Popular</span>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${tier.accent}1a`, color: tier.accent }}><Icon size={18} /></span>
                  <h3 className="font-display font-bold text-xl">{tier.name}</h3>
                </div>
                <p className="text-xs text-mentor-muted uppercase tracking-wider mb-4">{tier.tagline}</p>
                <div className="mb-5">
                  <span className="font-display font-black text-4xl">{monthlyVal === 0 ? 'Free' : `$${price}`}</span>
                  <span className="text-mentor-muted text-sm">{unit}</span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-mentor-text/90">
                      <Check size={16} className="text-signal-green shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button type="button" onClick={onRequestAccess} className={`w-full justify-center ${tier.featured ? 'btn-primary' : 'btn-secondary'}`}>
                  {tier.id === 'observer' ? 'Request access' : `Request access`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-mentor-muted mt-8 max-w-2xl mx-auto">
          Educational market-awareness tool, not personal financial advice. Access is invite-only — request early access and we&rsquo;ll review your spot, then send a secure invite.
        </p>
        <div className="text-center mt-4">
          <button type="button" onClick={onSignIn} className="text-sm text-mentor-muted hover:text-mentor-text underline-offset-4 hover:underline">
            Already a member? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
